import { OptionSelection, ProjectInput, RequirementInstance } from 'apiClient/Api';
import { RequirementItem, RequirementOption } from './calculatorApi';

/**
 * UI-Zustand einer konfigurierbaren Anforderungs-Instanz (Variante). `opt` ist – wie in der
 * Vanilla-Vorlage – eine flache Map über ALLE (auch verschachtelten) Options-IDs der Instanz;
 * die Verschachtelung kommt beim Sammeln aus den Options-Definitionen.
 */
export interface Instance {
    iid: number;
    menge: number;
    area: string;
    setName: string;
    opt: Record<string, number>;
}

export type InstanceMap = Record<string, Instance[]>;

let nextIid = 1;

export const newInstance = (menge = 0): Instance => ({
    iid: nextIid++,
    menge,
    area: 'A',
    setName: '',
    opt: {},
});

const collectOptions = (
    defs: RequirementOption[] | undefined,
    optMap: Record<string, number>,
): OptionSelection[] => {
    if (!defs) return [];
    const out: OptionSelection[] = [];
    for (const d of defs) {
        const m = optMap[d.requirementId] || 0;
        if (m > 0) out.push({ requirementId: d.requirementId, menge: m, options: collectOptions(d.options, optMap) });
    }
    return out;
};

/** Baut aus dem UI-Zustand das an die API zu sendende Projekt (nur Instanzen mit Menge > 0). */
export const collectProject = (
    projektname: string,
    instances: InstanceMap,
    index: Map<string, RequirementItem>,
): ProjectInput => {
    const instanzen: RequirementInstance[] = [];
    for (const reqId of Object.keys(instances)) {
        const item = index.get(reqId);
        for (const inst of instances[reqId]) {
            if (inst.menge > 0) {
                instanzen.push({
                    requirementId: reqId,
                    menge: inst.menge,
                    area: inst.area || 'A',
                    setName: inst.setName || null,
                    options: item ? collectOptions(item.options, inst.opt) : [],
                });
            }
        }
    }
    return { projektname, instanzen };
};

/** Stellt aus einem geladenen Projekt-JSON den UI-Zustand wieder her. */
export const projectToInstances = (project: ProjectInput): InstanceMap => {
    const map: InstanceMap = {};
    const flatten = (opts: OptionSelection[] | null | undefined, target: Record<string, number>) => {
        for (const o of opts || []) {
            if (o.requirementId) target[o.requirementId] = o.menge ?? 0;
            flatten(o.options, target);
        }
    };
    for (const inst of project.instanzen || []) {
        const reqId = inst.requirementId;
        if (!reqId) continue;
        if (!map[reqId]) map[reqId] = [];
        const created = newInstance(inst.menge ?? 0);
        created.area = inst.area || 'A';
        created.setName = inst.setName || '';
        flatten(inst.options, created.opt);
        map[reqId].push(created);
    }
    return map;
};

/** Set aller Anforderungs-IDs mit mindestens einer konfigurierten (Menge > 0) Instanz. */
export const configuredRequirementIds = (instances: InstanceMap): Set<string> => {
    const set = new Set<string>();
    for (const reqId of Object.keys(instances)) {
        if (instances[reqId].some((i) => i.menge > 0)) set.add(reqId);
    }
    return set;
};
